/**
 * EventBus — typed event emitter for the editor.
 *
 * Replaces all Vuex store.subscribe / store.commit patterns with a clean,
 * typed event system. All editor components communicate through this bus
 * rather than importing a global store.
 */

type EventHandler<T> = T extends void ? () => void : (payload: T) => void;
type WildcardHandler = (event: string, payload: unknown) => void;

export class EventBus<TEvents extends { [K in keyof TEvents]: unknown }> {
  private listeners = new Map<keyof TEvents, Set<EventHandler<unknown>>>();
  private wildcardListeners = new Set<WildcardHandler>();

  /**
   * Subscribe to a specific event.
   */
  on<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as EventHandler<unknown>);
  }

  /**
   * Subscribe to a specific event, firing the handler only once.
   */
  once<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
    const wrappedHandler = ((...args: unknown[]) => {
      this.off(event, wrappedHandler as EventHandler<TEvents[K]>);
      (handler as (...a: unknown[]) => void)(...args);
    }) as EventHandler<TEvents[K]>;
    this.on(event, wrappedHandler);
  }

  /**
   * Unsubscribe from a specific event.
   */
  off<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler<unknown>);
    }
  }

  /**
   * Emit an event with an optional payload.
   */
  emit<K extends keyof TEvents>(
    event: K,
    ...args: TEvents[K] extends void ? [] : [TEvents[K]]
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        (handler as (...a: unknown[]) => void)(args[0]);
      }
    }
    // Notify wildcard listeners
    for (const handler of this.wildcardListeners) {
      handler(event as string, args[0]);
    }
  }

  /**
   * Subscribe to all events (wildcard listener).
   */
  onAny(handler: WildcardHandler): void {
    this.wildcardListeners.add(handler);
  }

  /**
   * Unsubscribe a wildcard listener.
   */
  offAny(handler: WildcardHandler): void {
    this.wildcardListeners.delete(handler);
  }

  /**
   * Remove all listeners and clean up.
   */
  dispose(): void {
    this.listeners.clear();
    this.wildcardListeners.clear();
  }
}
